import { useState, useEffect } from "react";
import { ListControl } from "../common/Dialog";
import LangDateInput from "../LangDateInput";
import { DialogProps, TimeRange, TurbineInfo } from "../../types/dialog-types";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import {
  validateEndAfterStart,
  validateMaxDuration,
  formatTigerDateTime,
  tigerSecondsToDate,
  dateTimeToTigerSeconds,
  TIGER_TO_UNIX_OFFSET_SECS,
} from "../../utils/tigerTime";
import { useTigerMaxReplay } from "../../utils/tigerMaxReplay";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { getEventModelTimes } from "../../api/getEventModelTimes";
import { ReplayFromRequest } from "../../utils/loadDataReplay";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Sentinel dropdown value for the recent-times history (C++ "Time" entry). */
const TIME_OPTION = "__TIME__";
/** Replay loads historical data, so model times come from the HIST source. */
const REPLAY_SESSION = "HIST";

import { useTurbineSerials } from "../../utils/turbineSerial";

interface EventModel {
  id: string;
  name: string;
}

interface EventItem {
  startEpoch: number;
  endEpoch: number;
  startTime: string;
  endTime: string;
  duration: string;
}

async function fetchTurbineList(): Promise<TurbineInfo[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const json = await invoke<string>("get_config_list");
      return JSON.parse(json) as TurbineInfo[];
    }
    const response = await fetch("/api/get_turbine_list");
    const text = await response.text();
    if (!text) return [];
    const parsed = JSON.parse(text);
    return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
  } catch {
    return [];
  }
}

/** Event-model dropdown source — same as the multi-time graph dialog. */
async function fetchEventModels(config: string): Promise<EventModel[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const pairs = await invoke<Array<[string, string]>>("get_event_models", { configName: config });
      return pairs.map(([id, name]) => ({ id, name }));
    }
    const response = await fetch(`/api/get_event_models?config_name=${encodeURIComponent(config)}`);
    if (!response.ok) return [];
    const pairs = (await response.json()) as Array<[string, string]>;
    return pairs.map(([id, name]) => ({ id, name }));
  } catch {
    return [];
  }
}

/** Recent replay windows for this turbine (last 10, from the user profile).
 *  The backend returns them oldest-first (newest appended last); we reverse so the
 *  most recently replayed window sits at the top of the dialog's time list. */
async function fetchRecentReplay(config: string): Promise<Array<{ start: number; end: number }>> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const rows = await invoke<Array<{ config: string; start: number; end: number }>>(
        "get_recent_replay_data",
        { config },
      );
      return rows.map((r) => ({ start: r.start, end: r.end })).reverse();
    }
    const response = await fetch(`/api/get_recent_replay_data?config=${encodeURIComponent(config)}`);
    if (!response.ok) return [];
    const rows = (await response.json()) as Array<{ config: string; start: number; end: number }>;
    return rows.map((r) => ({ start: r.start, end: r.end })).reverse();
  } catch {
    return [];
  }
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

// Times here are TIGER SECONDS (since 1901), the units getEventModelTimes returns
// and the data server / load_replay_data expect — NOT Unix. (Treating them as Unix
// showed dates ~69 years in the future.) The pickers are decoded/encoded with the
// UTC getters (matching the graph dialogs' dateTimeToTigerSeconds), which is the
// zone-neutral read of a Tiger wall-clock value — what is typed and shown is the
// site's local time. See `nowTigerSecs` for the default window.

/** Tiger seconds → UTC `{date: YYYY-MM-DD, time: HH:MM:SS}` for the pickers. */
function tigerSecsToFields(tigerSecs: number): { date: string; time: string } {
  const d = tigerSecondsToDate(tigerSecs);
  return {
    date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`,
  };
}

function formatDuration(startTigerSecs: number, endTigerSecs: number): string {
  let secs = Math.max(0, endTigerSecs - startTigerSecs);
  const h = Math.floor(secs / 3600);
  secs -= h * 3600;
  const m = Math.floor(secs / 60);
  secs -= m * 60;
  return `${pad(h)}:${pad(m)}:${pad(secs)}`;
}

/** Build an EventItem from a getEventModelTimes occurrence (Tiger-second pair). */
function toEventItem(o: { start: number; end: number }): EventItem {
  return {
    startEpoch: o.start,
    endEpoch: o.end,
    startTime: formatTigerDateTime(o.start),
    endTime: formatTigerDateTime(o.end),
    duration: formatDuration(o.start, o.end),
  };
}

/**
 * "Now" as Tiger seconds. Tiger stamps samples with the machine's LOCAL
 * wall-clock encoded zone-neutrally, and `tigerSecsToFields` decodes with the
 * UTC getters to read that wall value back unshifted — so the clock reading we
 * feed it must be the local one. Taking `Date.now()` straight (UTC) opened the
 * dialog showing a UTC start/end, an hour or two off the local time the user
 * (and the data) is on.
 */
function nowTigerSecs(): number {
  const now = new Date();
  const localUnixSecs = Math.floor(now.getTime() / 1000) - now.getTimezoneOffset() * 60;
  return localUnixSecs + TIGER_TO_UNIX_OFFSET_SECS;
}

function currentTimeRange(): TimeRange {
  const nowTiger = nowTigerSecs();
  const start = tigerSecsToFields(nowTiger - 4 * 60);
  const end = tigerSecsToFields(nowTiger);
  return { startDate: start.date, startTime: start.time, endDate: end.date, endTime: end.time };
}

// Last turbine + window chosen via OK, restored on the next open. Mirrors the C++
// in-memory globals load_data_replay_config / _start_time / _end_time: per-session
// (not persisted to disk), reset to the 4-minute default only on first use. The
// event dropdown is intentionally NOT remembered — C++ always resets it to "Time".
let lastReplaySelection: { turbine: string; timeRange: TimeRange } | null = null;

export interface LoadDataDialogProps extends DialogProps {
  onOk?: (turbine: string, event: string, timeRange: TimeRange, selectedEvent: number) => void;
  onCancel?: () => void;
  /** One-shot pre-population (e.g. from a "Replay From" menu): turbine + window
   *  in Tiger seconds. Applied when the dialog opens; the user can then adjust. */
  seed?: ReplayFromRequest | null;
}

const styles = {
  ...acrylicStyles,
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 540,
    maxWidth: "92vw",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
  },
} as const;

export function LoadDataDialog({ open, onClose, onApply, onOk, onCancel, seed }: LoadDataDialogProps) {
  const [validationError, setValidationError] = useState("");
  const [turbines, setTurbines] = useState<TurbineInfo[]>([]);
  const [models, setModels] = useState<EventModel[]>([]);
  const [eventItems, setEventItems] = useState<EventItem[]>([]);
  const [turbine, setTurbine] = useState("");
  const [event, setEvent] = useState(TIME_OPTION);
  const [timeRange, setTimeRange] = useState<TimeRange>(currentTimeRange);
  const [selectedEventIndex, setSelectedEventIndex] = useState(-1);
  // Progress for the event-model occurrence fetch, mirroring the multi-time graph
  // dialog (GraphChannelSelectionDialog): "Loading event times…" while the model's
  // times are fetched, an error line on failure. Only for a selected model; the
  // "Time" (recent-replay) source is local/quick and shows no indicator.
  const [eventTimesLoading, setEventTimesLoading] = useState(false);
  const [eventTimesError, setEventTimesError] = useState("");
  const maxReplay = useTigerMaxReplay();

  // On open: restore the last OK'd turbine + window (C++ load_data_replay_*), or
  // default to the first turbine + a 4-minute window on first use. The event
  // dropdown always starts at "Time", matching C++ populate_event_model_list.
  useEffect(() => {
    if (!open) return;
    if (seed) {
      const s = tigerSecsToFields(seed.start);
      const e = tigerSecsToFields(seed.end);
      setTimeRange({ startDate: s.date, startTime: s.time, endDate: e.date, endTime: e.time });
    } else {
      setTimeRange(lastReplaySelection ? lastReplaySelection.timeRange : currentTimeRange());
    }
    setSelectedEventIndex(-1);
    setValidationError("");
    setEvent(TIME_OPTION);
    let cancelled = false;
    fetchTurbineList().then((list) => {
      if (cancelled) return;
      setTurbines(list);
      const want = seed?.config ?? lastReplaySelection?.turbine;
      const restore = want && list.some((t) => t.config_name === want);
      setTurbine(restore ? want! : list.length > 0 ? list[0].config_name : "");
    });
    return () => { cancelled = true; };
  }, [open, seed]);

  // Turbine change: reload the event-model dropdown (reset to "Time").
  useEffect(() => {
    if (!open || !turbine) return;
    let cancelled = false;
    fetchEventModels(turbine).then((list) => {
      if (!cancelled) setModels(list);
    });
    setEvent(TIME_OPTION);
    return () => { cancelled = true; };
  }, [open, turbine]);

  // Event/turbine change: "Time" → recent history; a model → its HIST times.
  // A model fetch can be slow (the data server walks the event log), so show the
  // same "Loading event times…" progress the multi-time graph dialog uses.
  useEffect(() => {
    if (!open || !turbine) return;
    let cancelled = false;
    const loadingModel = event !== TIME_OPTION;
    setEventTimesError("");
    setEventTimesLoading(loadingModel);
    // Clear the previous model/turbine's rows before the (possibly slow) fetch so
    // the list never shows the old model's times while the new ones load — it
    // empties immediately on the change, then fills once the new list arrives.
    setEventItems([]);
    setSelectedEventIndex(-1);
    const load = event === TIME_OPTION
      ? fetchRecentReplay(turbine)
      : getEventModelTimes(turbine, REPLAY_SESSION, event);
    Promise.resolve(load).then((rows) => {
      if (cancelled) return;
      setEventItems(rows.map(toEventItem));
      setSelectedEventIndex(-1);
      setEventTimesLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setEventItems([]);
      setEventTimesLoading(false);
      if (loadingModel) {
        setEventTimesError(String(e?.message ?? e ?? getTigerResource("TIGER_LOAD_EVENT_TIMES_ERROR")));
      }
    });
    return () => { cancelled = true; };
  }, [open, turbine, event]);

  // Serial-tracking sites (enable_turbine_serial) show the engine serial for
  // each row's start date instead of Duration — C++ load_data_shell zeroes the
  // Duration column and widens "Serial" (list col 3) when the flag is on.
  const { enabled: serialEnabled, serialFor } = useTurbineSerials(turbine, eventItems);
  const eventColumns = [
    { key: "startTime", label: getTigerResource("TIGER_RES_START_TIME"), width: 180 },
    { key: "endTime", label: getTigerResource("TIGER_RES_END_TIME"), width: 180 },
    serialEnabled
      ? { key: "serial", label: getTigerResource("TIGER_RES_SERIAL"), width: 100 }
      : { key: "duration", label: getTigerResource("TIGER_RES_DURATION"), width: 100 },
  ];

  const handleOk = () => {
    const start = dateTimeToTigerSeconds(timeRange.startDate, timeRange.startTime);
    const end = dateTimeToTigerSeconds(timeRange.endDate, timeRange.endTime);
    if (start == null || end == null) {
      setValidationError(getTigerResource("TIGER_LOAD_INVALID_DATETIME"));
      return;
    }
    const rangeErr = validateEndAfterStart(start, end);
    if (rangeErr) {
      setValidationError(rangeErr);
      return;
    }
    const durErr = validateMaxDuration(end - start, maxReplay);
    if (durErr) {
      setValidationError(durErr);
      return;
    }
    setValidationError("");
    // Remember the current selection so the next open restores it (C++
    // load_data_replay_config / _start_time / _end_time, set in OnLoadDataOkButton).
    lastReplaySelection = { turbine, timeRange: { ...timeRange } };
    // Remember this window for the turbine (last 10, like C++). On the web this
    // POSTs to the server, which persists it under the logged-in user's prefs.
    if (turbine) {
      if (isTauri()) {
        void import("@tauri-apps/api/core").then(({ invoke }) =>
          invoke("add_recent_replay_data", { config: turbine, start, end }),
        ).catch((e) => console.error("add_recent_replay_data failed:", e));
      } else {
        void fetch("/api/add_recent_replay_data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: turbine, start, end }),
        }).catch((e) => console.error("add_recent_replay_data failed:", e));
      }
    }
    onOk?.(turbine, event, timeRange, selectedEventIndex);
    onApply?.({ turbine, event, timeRange, selectedEventIndex });
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  const handleEventSelect = (index: number) => {
    setSelectedEventIndex(index);
    if (index >= 0 && index < eventItems.length) {
      const ev = eventItems[index];
      const s = tigerSecsToFields(ev.startEpoch);
      const e = tigerSecsToFields(ev.endEpoch);
      setTimeRange({ startDate: s.date, startTime: s.time, endDate: e.date, endTime: e.time });
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.dialog} role="dialog" aria-label={getTigerResource("TIGERUI_LOAD_DATA")} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{getTigerResource("TIGERUI_LOAD_DATA")}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Selection */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_LOAD_SELECTION")}</span>
            <div style={styles.card}>
              <div style={styles.fieldRow}>
                <label style={styles.label}>{getTigerResource("TIGER_RES_TURBINE")}</label>
                <select style={styles.select} value={turbine} onChange={(e) => setTurbine(e.target.value)}>
                  {turbines.map((t) => (
                    <option key={t.config_name} value={t.config_name}>
                      {t.turbine_name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.fieldRow}>
                <label style={styles.label}>{getTigerResource("TIGER_CHANNEL_EVENT")}</label>
                <select style={styles.select} value={event} onChange={(e) => setEvent(e.target.value)}>
                  <option value={TIME_OPTION}>{getTigerResource("TIGER_GRAPH_TIME")}</option>
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Time Range */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_LOAD_TIME_RANGE")}</span>
            <div style={styles.card}>
              <div style={styles.fieldRow}>
                <label style={styles.label}>{getTigerResource("TIGER_RES_START")}</label>
                <input
                  style={styles.input}
                  type="time"
                  step="1"
                  value={timeRange.startTime}
                  onChange={(e) => setTimeRange((prev) => ({ ...prev, startTime: e.target.value }))}
                />
                <LangDateInput
                  style={styles.input}
                  value={timeRange.startDate}
                  onChange={(iso) => setTimeRange((prev) => ({ ...prev, startDate: iso }))}
                />
              </div>
              <div style={styles.fieldRow}>
                <label style={styles.label}>{getTigerResource("TIGER_RES_END")}</label>
                <input
                  style={styles.input}
                  type="time"
                  step="1"
                  value={timeRange.endTime}
                  onChange={(e) => setTimeRange((prev) => ({ ...prev, endTime: e.target.value }))}
                />
                <LangDateInput
                  style={styles.input}
                  value={timeRange.endDate}
                  onChange={(iso) => setTimeRange((prev) => ({ ...prev, endDate: iso }))}
                />
              </div>
            </div>
          </div>

          {/* Events */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_OPC_EVENTS")}</span>
            {eventTimesLoading && (
              <div style={{ padding: 8, color: "#666" }}>{getTigerResource("TIGER_LOAD_EVENT_TIMES_LOADING")}</div>
            )}
            {!eventTimesLoading && eventTimesError && (
              <div role="alert" style={{ padding: 8, color: "#b00020" }}>
                {tigerFormat(getTigerResource("TIGER_LOAD_EVENT_TIMES_FAILED"), eventTimesError)}
              </div>
            )}
            <div style={styles.tableWrap}>
              <ListControl<EventItem>
                columns={eventColumns}
                items={eventItems}
                selectedIndex={selectedEventIndex}
                onSelect={handleEventSelect}
                height={180}
                getRow={(item) => ({
                  startTime: item.startTime,
                  endTime: item.endTime,
                  duration: item.duration,
                  serial: serialFor(item.startEpoch),
                })}
              />
            </div>
          </div>
        </div>

        {/* Validation */}
        {validationError && (
          <div style={{ padding: "0 24px 4px", color: "#dc2626", fontSize: 12 }}>{validationError}</div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={handleCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
          <button className="btn-acrylic-primary" onClick={handleOk}>
            {getTigerResource("TIGER_RES_OK")}
          </button>
        </div>
      </div>
    </div>
  );
}
